import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    enablePushNotifications,
    disablePushNotifications,
    sendTestPush,
    onPushNewMail,
    getNotificationPermission,
    isPushSupported,
} from "../lib/notifications";
import {
    Mail,
    Plus,
    Loader2,
    Inbox,
    PenSquare,
    Settings2,
    Bell,
    BellOff,
} from "lucide-react";
import TopNav from "../components/TopNav";
import { listEmailAccounts } from "../lib/api";
import EmptyState from "../components/email/EmptyState";
import MailboxLayout from "../components/email/MailboxLayout";
import AddAccountDialog from "../components/email/AddAccountDialog";
import SettingsDialog from "../components/email/SettingsDialog";
import ComposeDialog from "../components/email/ComposeDialog";
import ScanConfigDialog from "../components/email/ScanConfigDialog";

// EmailPage is the shell — it owns top-level account/folder state and
// orchestrates the modular dialogs in /components/email. Heavy lifting
// (mailbox list, message body, compose, scan-config, add-account)
// lives in dedicated sibling files.
export default function EmailPage() {
    const ALL_INBOXES = "__all__";
    // iter-108: persist the last-selected inbox + folder across sessions
    // so "All inboxes" (or whichever account the user was last viewing)
    // stays sticky on reload instead of snapping back to the first
    // account every time.
    const LS_ACCOUNT_KEY = "planflow.email.activeAccountId";
    const LS_FOLDER_KEY = "planflow.email.folder";

    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeAccountId, setActiveAccountId] = useState(() => {
        try { return localStorage.getItem(LS_ACCOUNT_KEY) || null; } catch { return null; }
    });
    const [folder, setFolder] = useState(() => {
        try { return localStorage.getItem(LS_FOLDER_KEY) || "INBOX"; } catch { return "INBOX"; }
    });
    const [addOpen, setAddOpen] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [composeState, setComposeState] = useState(null); // null | {mode, source?}
    const [scanCfgOpen, setScanCfgOpen] = useState(false);

    const isAllInboxes = activeAccountId === ALL_INBOXES;
    const activeAccount = useMemo(() => {
        if (isAllInboxes) {
            return {
                id: ALL_INBOXES,
                provider: "aggregate",
                email_address: "All inboxes",
                display_name: "All inboxes",
            };
        }
        return accounts.find((a) => a.id === activeAccountId) || null;
    }, [accounts, activeAccountId, isAllInboxes]);

    // iter-107: notification permission state + in-app chime listener.
    const [pushState, setPushState] = useState(() => {
        if (!isPushSupported()) return "unsupported";
        const perm = getNotificationPermission();
        if (perm === "denied") return "denied";
        if (perm === "granted" && localStorage.getItem("planflow.push_enabled") === "1") return "enabled";
        return "off";
    });

    useEffect(() => {
        // Re-subscribe silently on mount if the user previously enabled.
        if (pushState === "enabled") {
            enablePushNotifications().catch(() => {
                // Don't nag — token may have expired. UI will show OFF.
                setPushState("off");
            });
        }
        // Listen for new-mail forwards from SW even if app is foreground.
        const off = onPushNewMail((payload) => {
            toast.info(payload?.title || "New mail", {
                description: (payload?.body || "").slice(0, 140),
            });
        });
        return off;
        /* eslint-disable-next-line */
    }, []);

    const handleTogglePush = async () => {
        if (pushState === "enabled") {
            try {
                await disablePushNotifications();
                setPushState("off");
                toast.success("Notifications disabled");
            } catch (err) {
                toast.error(err?.message || "Couldn't disable");
            }
            return;
        }
        try {
            await enablePushNotifications();
            setPushState("enabled");
            toast.success("New-mail notifications are ON");
            // Fire a one-shot test push so the user sees it works.
            try { await sendTestPush(); } catch { /* ignore */ }
        } catch (err) {
            toast.error(err?.message || "Couldn't enable notifications");
            setPushState(getNotificationPermission() === "denied" ? "denied" : "off");
        }
    };

    const loadAccounts = async () => {
        try {
            const list = await listEmailAccounts();
            setAccounts(list);
            // iter-108: respect the persisted selection. Keep it if it's
            // still valid (either the All-Inboxes sentinel or an account
            // that's still in the list). Otherwise fall back to the first.
            setActiveAccountId((prev) => {
                if (prev === ALL_INBOXES) return prev;
                if (prev && list.some((a) => a.id === prev)) return prev;
                if (list.length === 0) return null;
                return list[0].id;
            });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't load accounts");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { loadAccounts(); /* eslint-disable-next-line */ }, []);

    // Persist active inbox + folder so the user's last layout is restored
    // next time they open the Email page.
    useEffect(() => {
        try {
            if (activeAccountId) localStorage.setItem(LS_ACCOUNT_KEY, activeAccountId);
            else localStorage.removeItem(LS_ACCOUNT_KEY);
        } catch { /* ignore quota / privacy-mode */ }
    }, [activeAccountId]);
    useEffect(() => {
        try { localStorage.setItem(LS_FOLDER_KEY, folder); } catch { /* ignore */ }
    }, [folder]);

    if (loading) {
        return (
            <div className="min-h-[100dvh] bg-[#05091A] text-white">
                <TopNav />
                <div className="flex items-center justify-center py-32 text-slate-500 text-sm">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading email…
                </div>
            </div>
        );
    }

    return (
        <div className="h-dvh bg-[#05091A] text-white flex flex-col">
            <TopNav />
            <main className="flex-1 overflow-y-auto max-w-7xl w-full mx-auto px-4 md:px-6 py-4 md:py-6 pb-40">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
                        <Mail className="w-6 h-6 text-[#00A3FF]" />
                        Email
                    </h1>
                    <div className="flex items-center gap-2 flex-wrap">
                        {accounts.length > 0 && (
                            <select
                                value={activeAccountId || ""}
                                onChange={(e) => setActiveAccountId(e.target.value)}
                                data-testid="email-account-switcher"
                                className="bg-[#0A1128] border border-[#2A3655] rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:border-[#00A3FF]"
                            >
                                {accounts.length > 1 && (
                                    <option value="__all__">📥 All inboxes ({accounts.length})</option>
                                )}
                                {accounts.map((a) => {
                                    const tag = a.auth_type === "oauth"
                                        ? (a.provider === "microsoft" ? "MS · " : "OAuth · ")
                                        : "";
                                    return (
                                        <option key={a.id} value={a.id}>{tag}{a.email_address}</option>
                                    );
                                })}
                            </select>
                        )}
                        {activeAccount && (
                            <>
                                <button
                                    onClick={() => setComposeState({ mode: "compose" })}
                                    data-testid="email-compose-new"
                                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm bg-[#00A3FF] hover:bg-[#3395FF] text-white text-xs font-bold uppercase tracking-wider"
                                >
                                    <PenSquare className="w-3.5 h-3.5" /> Compose
                                </button>
                                {!isAllInboxes && (
                                    <>
                                        <button
                                            onClick={() => setScanCfgOpen(true)}
                                            data-testid="email-scan-config-btn"
                                            title="Auto-scan inbox for incoming PDFs + auto-quote"
                                            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-sm border text-xs font-bold uppercase tracking-wider ${
                                                activeAccount?.scan_incoming
                                                    ? "border-[#00C853] text-[#00C853] hover:bg-[#00C853]/10"
                                                    : "border-[#2A3655] text-slate-300 hover:border-[#00A3FF] hover:text-[#00A3FF]"
                                            }`}
                                        >
                                            <Inbox className="w-3.5 h-3.5" />
                                            Scan {activeAccount?.scan_incoming ? "· ON" : ""}
                                        </button>
                                        <button
                                            onClick={() => setSettingsOpen(true)}
                                            data-testid="email-settings-btn"
                                            title="Signature + account settings"
                                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm border border-[#2A3655] hover:border-[#00A3FF] text-slate-300 hover:text-[#00A3FF] text-xs font-bold uppercase tracking-wider"
                                        >
                                            <Settings2 className="w-3.5 h-3.5" />
                                        </button>
                                    </>
                                )}
                            </>
                        )}
                        <button
                            onClick={() => setAddOpen(true)}
                            data-testid="email-add-account-btn"
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm bg-[#1C253C] hover:bg-[#223055] border border-[#2A3655] hover:border-[#00A3FF] text-xs font-bold uppercase tracking-wider text-slate-200 hover:text-white"
                        >
                            <Plus className="w-3.5 h-3.5 text-[#00A3FF]" /> Add account
                        </button>
                        {pushState !== "unsupported" && (
                            <button
                                onClick={handleTogglePush}
                                disabled={pushState === "denied"}
                                data-testid="email-push-toggle"
                                title={
                                    pushState === "denied"
                                        ? "Browser permission denied — re-enable in browser settings"
                                        : pushState === "enabled"
                                        ? "Notifications ON — click to disable"
                                        : "Enable lock-screen / drop-down notifications"
                                }
                                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-sm border text-xs font-bold uppercase tracking-wider ${
                                    pushState === "enabled"
                                        ? "border-[#00C853] text-[#00C853] hover:bg-[#00C853]/10"
                                        : pushState === "denied"
                                        ? "border-[#2A3655] text-slate-500 cursor-not-allowed"
                                        : "border-[#2A3655] text-slate-300 hover:border-[#00A3FF] hover:text-[#00A3FF]"
                                }`}
                            >
                                {pushState === "enabled" ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
                                {pushState === "enabled" ? "Alerts · ON" : pushState === "denied" ? "Blocked" : "Alerts"}
                            </button>
                        )}
                    </div>
                </div>

                {!activeAccount || (isAllInboxes && accounts.length === 0) ? (
                    <EmptyState onAdd={() => setAddOpen(true)} />
                ) : (
                    <MailboxLayout
                        account={activeAccount}
                        accounts={accounts}
                        isAllInboxes={isAllInboxes}
                        folder={folder}
                        onFolderChange={setFolder}
                        onCompose={(mode, source) => setComposeState({ mode, source })}
                    />
                )}
            </main>

            <AddAccountDialog
                open={addOpen}
                onOpenChange={setAddOpen}
                onCreated={(a) => {
                    setAccounts((p) => {
                        // Dedupe: replace if id already in list (re-connect),
                        // otherwise append.
                        if (p.find((x) => x.id === a.id)) {
                            return p.map((x) => x.id === a.id ? a : x);
                        }
                        return [...p, a];
                    });
                    setActiveAccountId(a.id);
                    setAddOpen(false);
                }}
            />
            <SettingsDialog
                open={settingsOpen}
                onOpenChange={setSettingsOpen}
                account={activeAccount}
                onUpdated={(next) => setAccounts((p) => p.map((a) => a.id === next.id ? next : a))}
                onDeleted={(id) => {
                    setAccounts((p) => p.filter((a) => a.id !== id));
                    setSettingsOpen(false);
                }}
            />
            <ComposeDialog
                state={composeState}
                onClose={() => setComposeState(null)}
                account={activeAccount}
                accounts={accounts}
                isAllInboxes={isAllInboxes}
            />
            <ScanConfigDialog
                open={scanCfgOpen}
                onOpenChange={setScanCfgOpen}
                account={activeAccount}
                onUpdated={(next) => setAccounts((p) => p.map((a) => a.id === next.id ? next : a))}
            />
        </div>
    );
}
